import React, { useEffect, useMemo, useState } from "react"
import { Link } from "react-router-dom"
import { useAuth0 } from "@auth0/auth0-react"
import { AlertCircle, Bell, BellRing, ExternalLink, Search, Sparkles, UserPlus, Users } from "lucide-react"
import { useFetchSubscriptions } from "../../hooks/DataFetching/useFetchSubscriptions"
import { useCampaignFollows } from "../../hooks/useCampaignFollows"
import { FEATURE_FLAGS, isFeatureEnabled } from "../../config/featureFlags"
import "../../styles/components/DashboardMemberships.css"

const isOpenCampaign = (campaign) => {
  const status = (campaign.status || "").toLowerCase()
  const expiration = campaign.expiration ? new Date(campaign.expiration) : null
  return status === "active" && (!expiration || expiration > new Date())
}

const formatDate = (value) => {
  if (!value) return "No deadline"
  return new Date(value).toLocaleDateString(undefined, { month: "short", day: "numeric" })
}

const DashboardMemberships = () => {
  const { subscriptions, loading } = useFetchSubscriptions()
  const { user, getAccessTokenSilently } = useAuth0()
  const { followedCampaigns, unfollowCampaign } = useCampaignFollows(user?.sub)
  const campaignVotingEnabled = isFeatureEnabled(FEATURE_FLAGS.campaignVoting)
  const [campaignsByGroup, setCampaignsByGroup] = useState([])
  const [votedCampaignIds, setVotedCampaignIds] = useState(new Set())
  const [campaignsLoading, setCampaignsLoading] = useState(false)
  const [campaignError, setCampaignError] = useState("")
  const apiUrl = process.env.REACT_APP_API_URL

  useEffect(() => {
    const fetchCampaignContext = async () => {
      if (!subscriptions?.length) {
        setCampaignsByGroup([])
        return
      }

      if (!campaignVotingEnabled) {
        setCampaignsByGroup([])
        setVotedCampaignIds(new Set())
        return
      }

      setCampaignsLoading(true)
      setCampaignError("")

      try {
        const token = await getAccessTokenSilently({
          authorizationParams: {
            audience: process.env.REACT_APP_API_AUDIENCE,
          },
        })

        const [voteHistoryResponse, campaignResponses] = await Promise.all([
          fetch(`${apiUrl}/api/Votes/user/history`, {
            headers: { Authorization: `Bearer ${token}` },
          }),
          Promise.all(
            subscriptions.map(async (subscription) => {
              const response = await fetch(`${apiUrl}/api/Campaigns/${subscription.groupId}`, {
                headers: { Authorization: `Bearer ${token}` },
              })

              if (!response.ok) {
                return []
              }

              const campaigns = await response.json()
              return (Array.isArray(campaigns) ? campaigns : []).map((campaign) => ({
                ...campaign,
                groupId: campaign.groupId || subscription.groupId,
                groupName: subscription.groupName || `Group ${subscription.groupId}`,
              }))
            }),
          ),
        ])

        if (voteHistoryResponse.ok) {
          const voteHistory = await voteHistoryResponse.json()
          setVotedCampaignIds(new Set((Array.isArray(voteHistory) ? voteHistory : []).map((vote) => Number(vote.campaignId))))
        }

        setCampaignsByGroup(campaignResponses.flat())
      } catch (error) {
        console.error("Error loading dashboard campaign context:", error)
        setCampaignError("Campaign alerts are unavailable right now.")
      } finally {
        setCampaignsLoading(false)
      }
    }

    fetchCampaignContext()
  }, [apiUrl, campaignVotingEnabled, getAccessTokenSilently, subscriptions])

  const campaignAlerts = useMemo(
    () =>
      campaignsByGroup
        .filter((campaign) => isOpenCampaign(campaign) && !votedCampaignIds.has(Number(campaign.id)))
        .sort((a, b) => new Date(a.expiration || "2999-01-01") - new Date(b.expiration || "2999-01-01"))
        .slice(0, 5),
    [campaignsByGroup, votedCampaignIds],
  )

  const followedWithLatestData = useMemo(
    () =>
      followedCampaigns.map((followed) => ({
        ...followed,
        ...(campaignsByGroup.find((campaign) => Number(campaign.id) === Number(followed.id)) || {}),
      })),
    [campaignsByGroup, followedCampaigns],
  )

  return (
    <section className="dashboard-memberships-panel">
      <div className="dashboard-memberships-header">
        <div>
          <span>Community radar</span>
          <h2>Memberships</h2>
          <p>Your groups, missed votes, and campaigns worth tracking.</p>
        </div>
      </div>

      <div className="dashboard-membership-actions" aria-label="Membership actions">
        <Link to="/fetch-groups">
          <Users size={16} aria-hidden="true" />
          Find a Colony
        </Link>
        <Link to="/user-search">
          <Search size={16} aria-hidden="true" />
          Find a Friend
        </Link>
        <Link to="/post-group">
          <Sparkles size={16} aria-hidden="true" />
          Start a Colony
        </Link>
      </div>

      <div className="dashboard-membership-section">
        <div className="dashboard-membership-section-title">
          <h3>My Memberships</h3>
          <span>{subscriptions?.length || 0}</span>
        </div>
        {loading ? (
          <div className="dashboard-membership-loading">Loading memberships...</div>
        ) : subscriptions?.length ? (
          <div className="dashboard-membership-list">
            {subscriptions.map((subscription) => (
              <Link key={subscription.id} to={`/group-members/${subscription.groupId}`} className="dashboard-membership-item">
                <span>
                  <strong>{subscription.groupName || `Group ${subscription.groupId}`}</strong>
                  <small>{subscription.groupType || subscription.groupCategory || "Community group"}</small>
                </span>
                <UserPlus size={16} aria-hidden="true" />
              </Link>
            ))}
          </div>
        ) : (
          <div className="dashboard-membership-empty">No memberships found.</div>
        )}
      </div>

      {campaignVotingEnabled && (
        <div className="dashboard-membership-section">
          <div className="dashboard-membership-section-title alert">
            <h3>Campaign Alerts</h3>
            <span>{campaignAlerts.length}</span>
          </div>
          {campaignsLoading ? (
            <div className="dashboard-membership-loading">Checking open campaigns...</div>
          ) : campaignError ? (
            <div className="dashboard-membership-empty warning">
              <AlertCircle size={16} aria-hidden="true" />
              {campaignError}
            </div>
          ) : campaignAlerts.length ? (
            <div className="dashboard-campaign-stack">
              {campaignAlerts.map((campaign) => (
                <Link key={campaign.id} to={`/group-members/${campaign.groupId}?tab=ideas&campaignId=${campaign.id}`} className="dashboard-campaign-alert">
                  <BellRing size={16} aria-hidden="true" />
                  <span>
                    <strong>{campaign.title}</strong>
                    <small>{campaign.groupName} · closes {formatDate(campaign.expiration)}</small>
                  </span>
                </Link>
              ))}
            </div>
          ) : (
            <div className="dashboard-membership-empty">No open campaigns need your vote.</div>
          )}
        </div>
      )}

      {campaignVotingEnabled && (
        <div className="dashboard-membership-section">
          <div className="dashboard-membership-section-title">
            <h3>Followed Campaigns</h3>
            <span>{followedWithLatestData.length}</span>
          </div>
          {followedWithLatestData.length ? (
            <div className="dashboard-campaign-stack">
              {followedWithLatestData.slice(0, 5).map((campaign) => (
                <div key={campaign.id} className="dashboard-followed-campaign">
                  <Bell size={16} aria-hidden="true" />
                  <span>
                    <strong>{campaign.title}</strong>
                    <small>{campaign.groupName} · {campaign.status || "Tracked"}</small>
                  </span>
                  <button type="button" onClick={() => unfollowCampaign(campaign.id)} aria-label={`Unfollow ${campaign.title}`}>
                    ×
                  </button>
                </div>
              ))}
            </div>
          ) : (
            <div className="dashboard-membership-empty">
              Follow campaigns from a group Ideas tab and they will appear here.
            </div>
          )}
        </div>
      )}

      <Link to="/fetch-groups" className="dashboard-membership-footer-link">
        Browse all groups
        <ExternalLink size={14} aria-hidden="true" />
      </Link>
    </section>
  )
}

export default DashboardMemberships
